import React from 'react';
import { useNotifications } from '../../context/NotificationContext';
import { timeAgo } from '../../utils/helpers';
import { useClickOutside, useMediaQuery } from '../../hooks';
import { useState, useCallback } from 'react';

const NotificationPanel = ({ notifications, onClose, onClear, isMobile }) => (
  <div style={{
    position: 'absolute', top: '100%', right: isMobile ? -40 : 0, marginTop: 8,
    width: isMobile ? 300 : 360, maxHeight: 480, overflowY: 'auto',
    background: 'var(--bg-surface)', border: '1px solid var(--border-light)',
    borderRadius: 'var(--radius-lg)', zIndex: 200,
    boxShadow: 'var(--shadow-lg)', animation: 'fadeIn 0.15s ease',
  }}>
    <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontSize: '0.8rem', fontWeight: 600, fontFamily: 'var(--font-mono)', letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>Live Events</span>
      {notifications.length > 0 && (
        <button onClick={onClear} style={{ fontSize: '0.7rem', color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}>Clear all</button>
      )}
    </div>
    {notifications.length === 0 ? (
      <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>No live events yet</div>
    ) : (
      notifications.map((n) => (
        <div key={n.id} style={{
          padding: '12px 16px', borderBottom: '1px solid var(--border)',
          display: 'flex', gap: 12, alignItems: 'flex-start',
        }}>
          <div style={{
            width: 8, height: 8, borderRadius: '50%', marginTop: 5, flexShrink: 0,
            background: n.type === 'open' ? 'var(--cyan)' : 'var(--amber)',
            boxShadow: n.type === 'open' ? '0 0 8px var(--cyan)' : '0 0 8px var(--amber)',
          }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2 }}>{n.title}</p>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.message}</p>
            <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2, fontFamily: 'var(--font-mono)' }}>{n.meta}</p>
          </div>
          <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', flexShrink: 0 }}>{timeAgo(n.timestamp)}</span>
        </div>
      ))
    )}
  </div>
);

export const PageHeader = ({ title, subtitle, actions }) => {
  const { notifications, unreadCount, markAllRead, clearAll } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useClickOutside(useCallback(() => setOpen(false), []));
  const isMobile = useMediaQuery('(max-width: 768px)');

  const toggle = () => {
    setOpen((o) => !o);
    if (!open) markAllRead();
  };

  return (
    <div style={{
      padding: isMobile ? '16px 20px 16px' : '28px 32px 20px',
      borderBottom: '1px solid var(--border)',
      display: 'flex', alignItems: isMobile ? 'center' : 'flex-start', justifyContent: 'space-between',
      position: 'sticky', top: 0, zIndex: 100,
      background: 'var(--bg-base)',
      backdropFilter: 'blur(8px)',
      flexWrap: isMobile ? 'wrap' : 'nowrap',
      gap: isMobile ? 12 : 0,
    }}>
      <div>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? '1.3rem' : '1.6rem', color: 'var(--text-primary)', lineHeight: 1.2, marginBottom: 4 }}>{title}</h1>
        {subtitle && <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{subtitle}</p>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12 }}>
        {actions && (
          <div style={{ transform: isMobile ? 'scale(0.9)' : 'scale(1)', transformOrigin: 'right center' }}>
            {actions}
          </div>
        )}
        {/* Bell */}
        <div ref={ref} style={{ position: 'relative' }}>
          <button onClick={toggle} style={{
            position: 'relative', width: 38, height: 38, borderRadius: 'var(--radius-md)',
            background: open ? 'var(--cyan-dim)' : 'var(--bg-elevated)',
            border: `1px solid ${open ? 'var(--border-accent)' : 'var(--border)'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', fontSize: '1rem', transition: 'all 150ms',
          }}>
            🔔
            {unreadCount > 0 && (
              <span style={{
                position: 'absolute', top: -4, right: -4,
                background: 'var(--red)', color: '#fff',
                fontSize: '0.6rem', fontFamily: 'var(--font-mono)',
                padding: '1px 5px', borderRadius: 10, lineHeight: 1.4,
                minWidth: 16, textAlign: 'center',
                animation: 'pulse-dot 1.5s infinite',
              }}>{unreadCount > 9 ? '9+' : unreadCount}</span>
            )}
          </button>
          {open && <NotificationPanel notifications={notifications} onClose={() => setOpen(false)} onClear={clearAll} isMobile={isMobile} />}
        </div>
      </div>
    </div>
  );
};
